import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import StopNameAutocomplete from '@/components/StopNameAutocomplete';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MapPin, Plus, Trash2, Loader2, Coffee, Utensils, Camera, Fuel, Pin,
  Cookie, Bath, ExternalLink, Sparkles, AlertTriangle,
} from 'lucide-react';
import { API } from '@/App';

/**
 * Per-day enroute-stops editor for the tour itinerary card.
 * View mode: compact pill list (Type · Name).
 * Edit mode: row-by-row editor with name, type dropdown, Google Maps URL,
 * an inline "Pin" button that hits /pollen/locations/upsert-stop and persists
 * the resolved coords back onto the stop.
 *
 * Storage shape on the tour: `itinerary[i].stops[]` — each stop has
 * `{ name, type, google_maps_url, lat, lng, notes, initiatives }`.
 *
 * Initiative tagging on stops is *tour-specific*. A Lunch stop that
 * doubles as a Smiles + Cultural Exchange story carries those tags only
 * on this tour — the underlying location/vendor record stays untouched.
 */

// Brand initiatives — same palette as the vendor-level picker so the
// halo colour on the share map matches whichever layer tagged the pin.
const STOP_INITS = [
  { key: 'smiles', label: 'Smiles', color: '#f59e0b', activeBg: 'bg-amber-500', activeText: 'text-white', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  { key: 'cultural-exchange', label: 'Cultural Exchange', color: '#c2410c', activeBg: 'bg-orange-700', activeText: 'text-white', bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
  { key: 'sustainability', label: 'Sustainability', color: '#84cc16', activeBg: 'bg-lime-600', activeText: 'text-white', bg: 'bg-lime-50', text: 'text-lime-800', border: 'border-lime-200' },
];
const INIT_COL = { 'smiles': '#f59e0b', 'cultural-exchange': '#c2410c', 'sustainability': '#84cc16' };
const INIT_LABEL = { 'smiles': 'Smiles', 'cultural-exchange': 'Cultural Exchange', 'sustainability': 'Sustainability' };

export const STOP_TYPES = ['POI', 'Breakfast', 'Tea Break', 'Lunch', 'Snacks', 'Fuel', 'Restroom', 'Other'];

const ICON_BY_TYPE = {
  POI: Pin,
  Breakfast: Coffee,
  'Tea Break': Coffee,
  Lunch: Utensils,
  Snacks: Cookie,
  Fuel: Fuel,
  Restroom: Bath,
  Other: Camera,
};
export const TINT_BY_TYPE = {
  POI: 'bg-violet-50 text-violet-700 border-violet-200',
  Breakfast: 'bg-amber-50 text-amber-700 border-amber-200',
  'Tea Break': 'bg-orange-50 text-orange-700 border-orange-200',
  Lunch: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Snacks: 'bg-rose-50 text-rose-700 border-rose-200',
  Fuel: 'bg-slate-50 text-slate-700 border-slate-200',
  Restroom: 'bg-sky-50 text-sky-700 border-sky-200',
  Other: 'bg-muted text-foreground border-border',
};

export const StopBadge = ({ stop }) => {
  const Icon = ICON_BY_TYPE[stop.type] || ICON_BY_TYPE.Other;
  const tint = TINT_BY_TYPE[stop.type] || TINT_BY_TYPE.Other;
  const hasCoords = stop.lat != null && stop.lng != null;
  return (
    <Badge
      variant="outline"
      className={`${tint} text-[10px] gap-1 font-normal py-0.5 pl-1 pr-1.5 inline-flex items-center max-w-full`}
      title={
        hasCoords
          ? `${stop.type || 'Other'}: ${stop.name}${stop.notes ? ` — ${stop.notes}` : ''}`
          : `⚠ ${stop.name} — not pinned. Paste a Google Maps URL in edit mode so this stop shows accurately on the customer's share map.`
      }
    >
      <Icon className="w-3 h-3 shrink-0" />
      <span className="truncate max-w-[140px]">{stop.name}</span>
      {(stop.initiatives || []).slice(0, 3).map(k => (
        <span key={k} title={INIT_LABEL[k] || k} className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: INIT_COL[k] || '#94a3b8' }} />
      ))}
      {hasCoords
        ? <MapPin className="w-2.5 h-2.5 shrink-0 text-emerald-600" />
        // Warning glyph — visually distinct from any initiative dot so
        // it can't be mistaken for a Smiles / Cultural Exchange tag.
        : <AlertTriangle className="w-2.5 h-2.5 shrink-0 text-rose-500" />
      }
    </Badge>
  );
};

const DayStopsEditor = ({ day, dayIndex, editing, editData, updateEdit }) => {
  const stops = day.stops || [];

  const setStops = (next) => {
    const itin = [...(editData.itinerary || [])];
    itin[dayIndex] = { ...itin[dayIndex], stops: next };
    updateEdit('itinerary', itin);
  };
  const addStop = () => setStops([...stops, { name: '', type: 'POI', google_maps_url: '', lat: null, lng: null, notes: '', initiatives: [] }]);
  const removeStop = (i) => setStops(stops.filter((_, idx) => idx !== i));
  const updateStop = (i, patch) => setStops(stops.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  if (!editing) {
    if (!stops.length) return null;
    return (
      <div className="flex items-start gap-2 flex-wrap pt-1">
        <span className="text-[11px] text-muted-foreground mt-0.5 shrink-0">Stops:</span>
        <div className="flex flex-wrap gap-1.5">
          {stops.map((s, i) => (
            <StopBadge key={i} stop={s} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t pt-2 mt-2" data-testid={`day-stops-editor-${dayIndex}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          Enroute stops {stops.length > 0 && `(${stops.length})`}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 text-[10px] px-2"
          onClick={addStop}
          data-testid={`day-stops-add-${dayIndex}`}
        >
          <Plus className="w-3 h-3 mr-1" /> Add stop
        </Button>
      </div>
      {(() => {
        // Operator hint when stops have URLs but were never pinned —
        // most common foot-gun (paste URL → forget to tap Pin → stop
        // silently disappears from share map). Auto-pin on blur is the
        // primary fix; this banner is a backup nudge for the rare cases
        // where blur didn't fire (e.g., user hit Save while still
        // focused in the URL field, or auto-pin call failed).
        const unpinnedWithUrl = stops.filter(s => (s.google_maps_url || '').trim() && (s.lat == null || s.lng == null));
        if (unpinnedWithUrl.length === 0) return null;
        return (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-[11px] text-amber-900" data-testid={`day-stops-unpinned-${dayIndex}`}>
            <span className="text-amber-600">⚠</span>
            <span className="flex-1">
              {unpinnedWithUrl.length} stop{unpinnedWithUrl.length > 1 ? 's have' : ' has'} URL pasted but not pinned. Tap the pulsing Pin button{unpinnedWithUrl.length > 1 ? 's' : ''} to confirm.
            </span>
          </div>
        );
      })()}
      {stops.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">No enroute stops added.</p>
      ) : (
        <div className="space-y-2">
          {stops.map((s, i) => (
            <StopRow
              key={i}
              dayIndex={dayIndex}
              index={i}
              stop={s}
              onChange={(patch) => updateStop(i, patch)}
              onRemove={() => removeStop(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StopRow = ({ dayIndex, index, stop, onChange, onRemove }) => {
  const [pinning, setPinning] = useState(false);

  const handlePin = async () => {
    if (!stop.name?.trim()) {
      toast.error('Enter the stop name first');
      return;
    }
    setPinning(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/upsert-stop`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({
          name: stop.name.trim(),
          subtype: stop.type || 'Other',
          google_maps_url: (stop.google_maps_url || '').trim(),
          // First-write-wins: the backend only persists `notes` onto
          // the canonical location row when the row is brand-new (or
          // its notes are still empty). Sending the field is safe.
          notes: (stop.notes || '').trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save stop');
      onChange({ lat: data.lat, lng: data.lng });
      if (data.lat != null) {
        toast.success(`${stop.name} → ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`);
      } else {
        toast.success(`${stop.name} saved (no coords yet — paste a Google Maps URL to pin)`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPinning(false);
    }
  };

  // Auto-pin on URL blur:
  // The earlier UX required the user to MANUALLY tap the Pin icon after
  // pasting a Google Maps URL. People pasted the URL and assumed they
  // were done — stops then silently dropped from the share map (real
  // user complaint, May 2026). Now: when the URL field loses focus,
  // if we have a name + a URL/lat-lng input + no coords yet, fire the
  // upsert automatically. Feels magical, no extra taps.
  const handleUrlBlur = () => {
    if (pinning) return;
    const url = (stop.google_maps_url || '').trim();
    if (!url) return;
    if (stop.lat != null && stop.lng != null) return;  // already pinned
    if (!stop.name?.trim()) return;  // need a name first
    handlePin();
  };

  const hasCoords = stop.lat != null && stop.lng != null;
  // Highlight the Pin button in amber when a URL is sitting unpinned —
  // visual nudge in case auto-pin failed / user disabled it.
  const urlEntered = !!(stop.google_maps_url || '').trim();
  const needsPin = urlEntered && !hasCoords && !pinning;

  return (
    <div
      className="p-2 rounded-md border bg-background space-y-1.5"
      data-testid={`stop-row-${dayIndex}-${index}`}
    >
      <div className="flex items-center gap-1.5">
        <StopNameAutocomplete
          value={stop.name || ''}
          dayIndex={dayIndex}
          index={index}
          onChangeName={(name) => {
            // Guard: operator pasted lat,lng or a URL into the Name field.
            // Redirect that value to the URL field so the row never lands
            // in the DB with a numeric "name", and show a friendly toast.
            const isCoords = /^\s*-?\d+(\.\d+)?\s*[,\s]\s*-?\d+(\.\d+)?\s*$/.test(name);
            const isUrl = name.startsWith('http://') || name.startsWith('https://');
            if (isCoords || isUrl) {
              onChange({ name: '', google_maps_url: name });
              toast.error(isCoords
                ? 'Coordinates moved to the URL field — type a real stop name (e.g. "Khasi Kitchen") above.'
                : 'URL moved to the URL field — type a real stop name above.');
              return;
            }
            onChange({ name });
          }}
          onPick={(picked) => {
            // Hydrate the stop with the canonical row's data:
            //   • coords (if pinned previously)
            //   • google_maps_url (so Open-in-Maps still goes to the
            //     real Google Place profile)
            //   • notes (first-write-wins). Operator can still edit
            //     notes for this tour — those edits stay tour-local
            //     and never overwrite the canonical row.
            const patch = {
              name: picked.name,
              lat: picked.lat,
              lng: picked.lng,
              google_maps_url: picked.google_maps_url || '',
            };
            // Only seed notes if the stop's notes are still empty,
            // so re-picking from autocomplete doesn't blow away an
            // edit the operator just typed.
            if (!(stop.notes || '').trim() && picked.notes) {
              patch.notes = picked.notes;
            }
            if (picked.type) patch.type = picked.type;
            onChange(patch);
          }}
        />
        <Select value={stop.type || 'POI'} onValueChange={(v) => onChange({ type: v })}>
          <SelectTrigger className="h-7 text-xs w-[110px]" data-testid={`stop-type-${dayIndex}-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STOP_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-destructive shrink-0"
          onClick={onRemove}
          data-testid={`stop-remove-${dayIndex}-${index}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          placeholder='Google Maps URL or "lat, lng"'
          value={stop.google_maps_url || ''}
          onChange={(e) => onChange({ google_maps_url: e.target.value })}
          onBlur={handleUrlBlur}
          className="h-7 text-xs flex-1"
          data-testid={`stop-url-${dayIndex}-${index}`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={`h-7 text-[10px] px-2 shrink-0 ${needsPin ? 'border-amber-400 bg-amber-50 hover:bg-amber-100 animate-pulse' : ''}`}
          onClick={handlePin}
          disabled={pinning || !stop.name?.trim()}
          data-testid={`stop-pin-${dayIndex}-${index}`}
          title={needsPin ? 'URL pasted but not yet pinned — click to confirm' : (hasCoords ? 'Pinned' : 'Pin this stop')}
        >
          {pinning ? <Loader2 className="w-3 h-3 animate-spin" /> : (hasCoords ? <MapPin className="w-3 h-3 text-emerald-600" /> : <Pin className={`w-3 h-3 ${needsPin ? 'text-amber-700' : ''}`} />)}
        </Button>
        {hasCoords && (
          <a
            href={`https://www.google.com/maps/?q=${stop.lat},${stop.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary p-1"
            title="Open in Google Maps"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <Textarea
        placeholder="Notes (optional — line breaks supported, will show as paragraphs on the share map)"
        value={stop.notes || ''}
        onChange={(e) => onChange({ notes: e.target.value })}
        rows={2}
        className="text-xs"
        data-testid={`stop-notes-${dayIndex}-${index}`}
      />
      {/* Tour-level Pollen Dot of Care — these initiatives are stored on
          the stop itself, NOT on the underlying location/vendor. So the
          same place can wear a different badge on a different tour. */}
      <div className="flex items-center gap-1 flex-wrap pt-0.5" data-testid={`stop-inits-${dayIndex}-${index}`}>
        <Sparkles className="w-3 h-3 text-amber-600 shrink-0" />
        <span className="text-[10px] text-muted-foreground mr-0.5">Pollen Dot of Care:</span>
        {STOP_INITS.map(it => {
          const on = (stop.initiatives || []).includes(it.key);
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => {
                const cur = new Set(stop.initiatives || []);
                if (cur.has(it.key)) cur.delete(it.key); else cur.add(it.key);
                onChange({ initiatives: Array.from(cur) });
              }}
              data-testid={`stop-init-${it.key}-${dayIndex}-${index}`}
              title={on ? `Click to remove ${it.label}` : `Tag this stop as ${it.label}`}
              className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors leading-none ${on ? `${it.activeBg} ${it.activeText} border-transparent` : `${it.bg} ${it.text} ${it.border} hover:opacity-80`}`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: on ? '#fff' : it.color }} />
              {it.label}
              {on && <span className="ml-1 opacity-80">×</span>}
            </button>
          );
        })}
      </div>
      {hasCoords && (
        <p className="text-[10px] text-muted-foreground font-mono">
          {stop.lat.toFixed(4)}, {stop.lng.toFixed(4)}
        </p>
      )}
    </div>
  );
};

export default DayStopsEditor;
